import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('benivet_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Authentication
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const logout = () => api.post('/auth/logout');
export const getCurrentUser = () => api.get('/auth/me');
export const getUsers = () => api.get('/users');
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Staff
export const getStaff = (params) => api.get('/staff', { params });
export const getStaffMember = (id) => api.get(`/staff/${id}`);
export const createStaff = (data) => api.post('/staff', data);
export const updateStaff = (id, data) => api.put(`/staff/${id}`, data);
export const deleteStaff = (id) => api.delete(`/staff/${id}`);
export const createStaffUser = (staffId, data) => api.post(`/staff/${staffId}/create-user`, data);
export const deleteStaffUser = (staffId) => api.delete(`/staff/${staffId}/delete-user`);
export const changeStaffPassword = (staffId, newPassword) => api.put(`/staff/${staffId}/change-password`, null, { params: { new_password: newPassword } });

// Patients
export const getPatients = (params) => api.get('/patients', { params });
export const getPatient = (id) => api.get(`/patients/${id}`);
export const createPatient = (data) => api.post('/patients', data);
export const updatePatient = (id, data) => api.put(`/patients/${id}`, data);
export const deletePatient = (id) => api.delete(`/patients/${id}`);
export const importPatientsFromExcel = (formData) => api.post('/patients/import-excel-file', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Consultations / History
export const getConsultations = (params) => api.get('/consultations', { params });
export const getConsultation = (id) => api.get(`/consultations/${id}`);
export const createConsultation = (data) => api.post('/consultations', data);
export const updateConsultation = (id, data) => api.put(`/consultations/${id}`, data);
export const deleteConsultation = (id) => api.delete(`/consultations/${id}`);

// Anesthesia Records
export const getAnesthesiaRecords = (params) => api.get('/anesthesia-records', { params });
export const getAnesthesiaRecord = (id) => api.get(`/anesthesia-records/${id}`);
export const createAnesthesiaRecord = (data) => api.post('/anesthesia-records', data);
export const updateAnesthesiaRecord = (id, data) => api.put(`/anesthesia-records/${id}`, data);
export const deleteAnesthesiaRecord = (id) => api.delete(`/anesthesia-records/${id}`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const handoffTask = (id, data) => api.post(`/tasks/${id}/handoff`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Shifts
export const getShifts = (params) => api.get('/shifts', { params });
export const getActiveShift = () => api.get('/shifts/active');
export const createShift = (data) => api.post('/shifts', data);
export const updateShift = (id, data) => api.put(`/shifts/${id}`, data);
export const deleteShift = (id) => api.delete(`/shifts/${id}`);

// Shift Notes
export const getShiftNotes = (params) => api.get('/shift-notes', { params });
export const createShiftNote = (data) => api.post('/shift-notes', data);

// Discharge Checklists
export const getDischargeChecklists = (params) => api.get('/discharge-checklists', { params });
export const getDischargeChecklist = (id) => api.get(`/discharge-checklists/${id}`);
export const createDischargeChecklist = (data) => api.post('/discharge-checklists', data);
export const updateDischargeChecklist = (id, data) => api.put(`/discharge-checklists/${id}`, data);

// Review Requests
export const getReviewRequests = (params) => api.get('/review-requests', { params });
export const createReviewRequest = (data) => api.post('/review-requests', data);
export const updateReviewRequest = (id, data) => api.put(`/review-requests/${id}`, data);
export const deleteReviewRequest = (id) => api.delete(`/review-requests/${id}`);

// AI Report
export const generateAIReport = (data) => api.post('/ai/generate-report', data);

// AI Image Analysis
export const analyzeImage = (data) => api.post('/ai/analyze-image', data);

// Voice / Transcription
export const transcribeAudio = (formData) => api.post('/voice/transcribe', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const processVoiceCommand = (data) => api.post('/voice/command', data);

// Dashboard
export const getDashboardSummary = () => api.get('/dashboard/summary');

// Config
export const getZones = () => api.get('/config/zones');
export const getTags = () => api.get('/config/tags');
export const getRoles = () => api.get('/config/roles');
export const getPriorities = () => api.get('/config/priorities');
export const getStatuses = () => api.get('/config/statuses');
export const getHospitalizationTypes = () => api.get('/config/hospitalization-types');
export const getShiftTypes = () => api.get('/config/shift-types');
export const getASAClasses = () => api.get('/config/asa-classes');
export const getOrderStatuses = () => api.get('/config/order-statuses');
export const getLabSampleStatuses = () => api.get('/config/lab-sample-statuses');

// Suppliers
export const getSuppliers = (params) => api.get('/suppliers', { params });
export const getSupplier = (id) => api.get(`/suppliers/${id}`);
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Articles
export const getArticles = (params) => api.get('/articles', { params });
export const createArticle = (data) => api.post('/articles', data);
export const updateArticle = (id, data) => api.put(`/articles/${id}`, data);
export const deleteArticle = (id) => api.delete(`/articles/${id}`);
export const importArticlesFromExcel = (formData) => api.post('/articles/import-excel', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const createOrdersFromArticles = (data) => api.post('/orders/create-from-articles', data);

// Laboratories
export const getLaboratories = (params) => api.get('/laboratories', { params });
export const getLaboratory = (id) => api.get(`/laboratories/${id}`);
export const createLaboratory = (data) => api.post('/laboratories', data);
export const updateLaboratory = (id, data) => api.put(`/laboratories/${id}`, data);
export const deleteLaboratory = (id) => api.delete(`/laboratories/${id}`);

// Lab Samples
export const getLabSamples = (params) => api.get('/lab-samples', { params });
export const getLabSample = (id) => api.get(`/lab-samples/${id}`);
export const createLabSample = (data) => api.post('/lab-samples', data);
export const updateLabSample = (id, data) => api.put(`/lab-samples/${id}`, data);
export const deleteLabSample = (id) => api.delete(`/lab-samples/${id}`);

// Medication Schedules
export const getMedicationSchedules = (params) => api.get('/medication-schedules', { params });
export const createMedicationSchedule = (data) => api.post('/medication-schedules', data);
export const deleteMedicationSchedule = (id) => api.delete(`/medication-schedules/${id}`);

// Medication Doses
export const getMedicationDoses = (params) => api.get('/medication-doses', { params });
export const getPendingMedicationDoses = () => api.get('/medication-doses/pending');
export const getUpcomingMedicationDoses = (hours = 4) => api.get('/medication-doses/upcoming', { params: { hours } });
export const updateMedicationDose = (id, data) => api.put(`/medication-doses/${id}`, data);

// CRI (Continuous Rate Infusion)
export const getCRIPresets = (params) => api.get('/cri-presets', { params });
export const createCRIPreset = (data) => api.post('/cri-presets', data);
export const deleteCRIPreset = (id) => api.delete(`/cri-presets/${id}`);
export const calculateCRI = (data) => api.post('/cri-calculations', data);
export const getCRICalculations = (params) => api.get('/cri-calculations', { params });

// Clinic Medications Database (for discharge instructions)
export const getClinicMedications = (params) => api.get('/clinic-medications', { params });
export const getClinicMedication = (id) => api.get(`/clinic-medications/${id}`);
export const createClinicMedication = (data) => api.post('/clinic-medications', data);
export const updateClinicMedication = (id, data) => api.put(`/clinic-medications/${id}`, data);
export const deleteClinicMedication = (id) => api.delete(`/clinic-medications/${id}`);
export const extractMedicationFromImage = (imageBase64) => api.post('/clinic-medications/extract-from-image', { image_base64: imageBase64 });
export const getMedicationAIInstructions = (id) => api.post(`/clinic-medications/${id}/ai-instructions`);
export const searchCimavet = (query) => api.get(`/clinic-medications/search-cimavet/${encodeURIComponent(query)}`);
export const calculateMedicationDose = (medicationId, patientWeightKg, doseMgKg) => 
  api.post('/calculate-dose', { medication_id: medicationId, patient_weight_kg: patientWeightKg, dose_mg_kg: doseMgKg });

// Voice Assistant
export const sendVoiceCommand = (data) => api.post('/ai/voice-assistant', data);

// Text to Speech
export const textToSpeech = (text) => {
  const formData = new FormData();
  formData.append('text', text);
  return api.post('/ai/text-to-speech', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// PDF Generation
export const generatePDF = (data) => api.post('/pdf/generate', data, { responseType: 'blob' });

// ========================
// NEW API FUNCTIONS - Iteration 8
// ========================

// Checklist Templates
export const getChecklistTemplates = () => api.get('/checklist-templates');
export const createChecklistTemplate = (data) => api.post('/checklist-templates', data);
export const updateChecklistTemplate = (id, data) => api.put(`/checklist-templates/${id}`, data);
export const deleteChecklistTemplate = (id) => api.delete(`/checklist-templates/${id}`);

// Referring Clinics (Clínicas Remisoras)
export const getReferringClinics = () => api.get('/referring-clinics');
export const createReferringClinic = (data) => api.post('/referring-clinics', data);
export const updateReferringClinic = (id, data) => api.put(`/referring-clinics/${id}`, data);
export const deleteReferringClinic = (id) => api.delete(`/referring-clinics/${id}`);

// Referral Cases (Casos Remitidos)
export const getReferralCases = (params) => api.get('/referral-cases', { params });
export const getReferralCase = (id) => api.get(`/referral-cases/${id}`);
export const createReferralCase = (data) => api.post('/referral-cases', data);
export const updateReferralCase = (id, data) => api.put(`/referral-cases/${id}`, data);
export const deleteReferralCase = (id) => api.delete(`/referral-cases/${id}`);
export const generateReferralReport = (id) => api.post(`/referral-cases/${id}/generate-report`);

// Digitized Invoices (Facturas Digitalizadas)
export const getDigitizedInvoices = (params) => api.get('/digitized-invoices', { params });
export const createDigitizedInvoice = (data) => api.post('/digitized-invoices', data);
export const updateDigitizedInvoice = (id, data) => api.put(`/digitized-invoices/${id}`, data);
export const deleteDigitizedInvoice = (id) => api.delete(`/digitized-invoices/${id}`);
export const bulkUpdateInvoiceStatus = (invoiceIds, isAccounted) => 
  api.post('/digitized-invoices/bulk-update-status', null, { params: { invoice_ids: invoiceIds, is_accounted: isAccounted } });
export const extractInvoiceData = (image_base64) => {
  const formData = new FormData();
  formData.append('image_base64', image_base64);
  return api.post('/digitized-invoices/extract-data', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Authorization Documents (Autorizaciones)
export const getAuthorizations = (params) => api.get('/authorizations', { params });
export const createAuthorization = (data) => api.post('/authorizations', data);
export const deleteAuthorization = (id) => api.delete(`/authorizations/${id}`);

// Vet Commission Settings
export const getVetCommissions = () => api.get('/vet-commissions');
export const createVetCommission = (data) => api.post('/vet-commissions', data);
export const updateVetCommission = (id, percentage) => api.put(`/vet-commissions/${id}`, null, { params: { commission_percentage: percentage } });
export const deleteVetCommission = (id) => api.delete(`/vet-commissions/${id}`);

// Emergency Commissions
export const getEmergencyCommissions = (params) => api.get('/emergency-commissions', { params });
export const createEmergencyCommission = (data) => api.post('/emergency-commissions', data);
export const updateEmergencyCommission = (id, items) => api.put(`/emergency-commissions/${id}`, items);
export const deleteEmergencyCommission = (id) => api.delete(`/emergency-commissions/${id}`);
export const extractAlbaranForCommission = (image_base64) => {
  const formData = new FormData();
  formData.append('image_base64', image_base64);
  return api.post('/emergency-commissions/extract-albaran', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Emergency Schedule (Guardias)
export const getEmergencySchedule = (params) => api.get('/emergency-schedule', { params });
export const createEmergencySchedule = (data) => api.post('/emergency-schedule', data);
export const deleteEmergencySchedule = (id) => api.delete(`/emergency-schedule/${id}`);

// Staff Colors
export const getStaffColors = () => api.get('/staff-colors');
export const saveStaffColor = (data) => api.post('/staff-colors', data);

// AI Reports History
export const getAIReportsHistory = (params) => api.get('/ai-reports-history', { params });
export const saveAIReportHistory = (data) => api.post('/ai-reports-history', data);
export const deleteAIReportHistory = (id) => api.delete(`/ai-reports-history/${id}`);

// Vacations
export const getVacations = (params) => api.get('/vacations', { params });
export const createVacation = (data) => api.post('/vacations', data);
export const updateVacation = (id, data) => api.put(`/vacations/${id}`, data);
export const deleteVacation = (id) => api.delete(`/vacations/${id}`);

// Suggestions
export const getSuggestions = (params) => api.get('/suggestions', { params });
export const createSuggestion = (data) => api.post('/suggestions', data);
export const updateSuggestion = (id, data) => api.put(`/suggestions/${id}`, null, { params: data });
export const deleteSuggestion = (id) => api.delete(`/suggestions/${id}`);

// Monthly Reports
export const getMonthlyReports = (params) => api.get('/reports/monthly', { params });
export const getMonthlyReport = (year, month) => api.get(`/reports/monthly/${year}/${month}`);
export const generateMonthlyReport = (year, month) => api.post(`/reports/generate/${year}/${month}`);

// Hospitalization Reports
export const generateHospitalizationReport = (patientId, includeImages = true) => 
  api.post(`/patients/${patientId}/generate-hospitalization-report?include_images=${includeImages}`);
export const generateDischargeReport = (patientId) => 
  api.post(`/patients/${patientId}/generate-discharge-report`);
export const addHospitalizationImage = (patientId, data) => 
  api.post(`/patients/${patientId}/add-hospitalization-image`, data);
export const deleteHospitalizationImage = (patientId, imageId) => 
  api.delete(`/patients/${patientId}/hospitalization-images/${imageId}`);

// Anesthesia Reports
export const generateAnesthesiaReport = (recordId, includeImages = true) => 
  api.post(`/anesthesia-records/${recordId}/generate-report?include_images=${includeImages}`);
export const addAnesthesiaImage = (recordId, data) => 
  api.post(`/anesthesia-records/${recordId}/add-image`, data);

// Integrated Patient History
export const getPatientFullHistory = (patientId) => api.get(`/patients/${patientId}/full-history`);
export const generateIntegratedReport = (patientId, data) => 
  api.post(`/patients/${patientId}/generate-integrated-report`, data);
export const generateSmartSummary = (patientId) => 
  api.post(`/patients/${patientId}/generate-smart-summary`);

// AI Transcription
export const transcribeConsultationAudio = (data) => api.post('/ai/transcribe', data);
export const analyzeImageIntelligent = (data) => api.post('/ai/analyze-intelligent', data);

// Discharge Instructions (using clinic-medications endpoints defined above)
export const uploadMedicationsExcel = (data) => api.post('/clinic-medications/bulk', data);
export const deleteMedication = (id) => api.delete(`/clinic-medications/${id}`);
export const duplicateClinicMedication = (id) => api.post(`/clinic-medications/${id}/duplicate`);

// Treatment Presets
export const getTreatmentPresets = (params) => api.get('/treatment-presets', { params });
export const getTreatmentPreset = (id) => api.get(`/treatment-presets/${id}`);
export const createTreatmentPreset = (data) => api.post('/treatment-presets', data);
export const updateTreatmentPreset = (id, data) => api.put(`/treatment-presets/${id}`, data);
export const deleteTreatmentPreset = (id) => api.delete(`/treatment-presets/${id}`);
export const duplicateTreatmentPreset = (id) => api.post(`/treatment-presets/${id}/duplicate`);

// Patient Treatment Reports
export const getPatientTreatmentReports = (params) => api.get('/patient-treatment-reports', { params });
export const createPatientTreatmentReport = (data) => api.post('/patient-treatment-reports', data);
export const updatePatientTreatmentReport = (reportId, textoEditado) => 
  api.put(`/patient-treatment-reports/${reportId}`, null, { params: { texto_editado_final: textoEditado } });

// AI Treatment Report Generation
export const generateTreatmentReport = (data) => api.post('/generate-treatment-report', data);

// AI Feedback / Learning
export const learnFromAIFeedback = (data) => api.post('/ai-feedback/learn', data);
export const getAIFeedbackGuidelines = (params) => api.get('/ai-feedback/guidelines', { params });
export const deleteAIFeedbackGuideline = (id) => api.delete(`/ai-feedback/guidelines/${id}`);

// Legacy
export const generateDischargeInstructions = (data) => api.post('/ai/generate-discharge-instructions', data);

// Follow-up Reminders
export const getFollowUpReminders = (params) => api.get('/follow-up-reminders', { params });
export const createFollowUpReminder = (data) => api.post('/follow-up-reminders', data);
export const updateFollowUpReminder = (id, data) => api.put(`/follow-up-reminders/${id}`, data);
export const deleteFollowUpReminder = (id) => api.delete(`/follow-up-reminders/${id}`);
export const completeFollowUpReminder = (id) => api.post(`/follow-up-reminders/${id}/complete`);

// Vademecum Import
export const getVademecumCategories = () => api.get('/vademecum/categories');
export const getMedicationsByCategory = (category, limit, skip) => 
  api.get(`/vademecum/by-category/${category}`, { params: { limit, skip } });
export const importCimavetBatch = (category) => 
  api.post('/vademecum/import-cimavet-batch', null, { params: { category } });
export const importAllCimavetCategories = () => 
  api.post('/vademecum/import-all-categories');
export const getVademecumImportStatus = () => api.get('/vademecum/import-status');
export const searchCimaHumana = (query) => api.get(`/vademecum/search-cima-humana/${encodeURIComponent(query)}`);
export const saveHumanMedication = (data) => api.post('/vademecum/save-human-medication', data);

// Email Templates
export const getEmailTemplates = () => api.get('/email-templates');
export const createEmailTemplate = (data) => api.post('/email-templates', data);
export const updateEmailTemplate = (id, data) => api.put(`/email-templates/${id}`, data);
export const deleteEmailTemplate = (id) => api.delete(`/email-templates/${id}`);

// Share
export const shareViaEmail = (data) => api.post('/share/email', data);

// Consent Templates
export const getConsentTemplates = (consentType) => api.get('/consent-templates', { params: { consent_type: consentType } });
export const getConsentTemplate = (id) => api.get(`/consent-templates/${id}`);
export const createConsentTemplate = (data) => api.post('/consent-templates', data);
export const updateConsentTemplate = (id, data) => api.put(`/consent-templates/${id}`, data);
export const deleteConsentTemplate = (id) => api.delete(`/consent-templates/${id}`);
export const seedDefaultConsentTemplates = () => api.post('/consent-templates/seed-defaults');

// Surgeries (Surgical Logbook)
export const getSurgeries = () => api.get('/surgeries');
export const createSurgery = (data) => api.post('/surgeries', data);
export const updateSurgery = (id, data) => api.put(`/surgeries/${id}`, data);
export const deleteSurgery = (id) => api.delete(`/surgeries/${id}`);
export const getSurgeryStats = () => api.get('/surgeries/stats');
export const exportSurgeriesPdf = () => `${API}/surgeries/pdf/all`;
export const exportSurgeryStatsPdf = () => `${API}/surgeries/pdf/stats`;

// Surgery Objectives
export const getSurgeryObjectives = () => api.get('/surgery-objectives');

// Referral Stats
export const getReferralStats = () => api.get('/referral-cases/stats');
export const createSurgeryObjective = (data) => api.post('/surgery-objectives', data);
export const updateSurgeryObjective = (id, data) => api.put(`/surgery-objectives/${id}`, data);
export const deleteSurgeryObjective = (id) => api.delete(`/surgery-objectives/${id}`);

// Alerts
export const getAlerts = () => api.get('/alerts');
export const getAlertConfig = () => api.get('/alert-config');
export const updateAlertConfig = (data) => api.put('/alert-config', data);
export const dismissAlert = (alertId) => api.post('/alerts/dismiss', { alert_id: alertId });
export const clearDismissedAlerts = () => api.delete('/alerts/dismissed');

// Task Comments
export const getTaskComments = (taskId) => api.get(`/tasks/${taskId}/comments`);
export const addTaskComment = (taskId, data) => api.post(`/tasks/${taskId}/comments`, data);

// Auxiliares
export const getAuxiliares = () => api.get('/auxiliares');
export const getAuxiliar = (id) => api.get(`/auxiliares/${id}`);
export const updateAuxiliar = (id, data) => api.put(`/auxiliares/${id}`, data);
export const exportAuxiliarPdf = (id) => `${API}/auxiliares/pdf/${id}`;
export const exportAllAuxiliaresPdf = () => `${API}/auxiliares/pdf/all`;
export const exportAuxiliaresMatrixPdf = () => `${API}/auxiliares/pdf/matrix`;

// Vacaciones
export const getVacaciones = (params) => api.get('/vacaciones', { params });
export const createVacacion = (data) => api.post('/vacaciones', data);
export const updateVacacion = (id, data) => api.put(`/vacaciones/${id}`, data);
export const deleteVacacion = (id) => api.delete(`/vacaciones/${id}`);
export const approveVacacion = (id, data) => api.post(`/vacaciones/${id}/approve`, data);
export const rejectVacacion = (id, data) => api.post(`/vacaciones/${id}/reject`, data);
export const getVacacionesStats = (year) => api.get(`/vacaciones/stats?year=${year}`);

// Pedidos - Best Cost Summary
export const getBestCost = () => api.get('/purchase-records/best-cost');
// Pedidos - Price Trend
export const getPriceTrend = (article, supplier) => {
  const params = { article };
  if (supplier) params.supplier = supplier;
  return api.get('/purchase-records/trend', { params });
};

// Catalogo Centauro
export const getCentauroCatalog = (params) => api.get('/catalogo/centauro', { params });
export const getCentauroStats = () => api.get('/catalogo/centauro/stats');
export const startCentauroSync = () => api.post('/catalogo/centauro/sync');
export const getCentauroSyncStatus = () => api.get('/catalogo/centauro/sync/status');
export const getCentauroChanges = (limit = 100) => api.get('/catalogo/centauro/changes', { params: { limit } });

// Fichajes (Control Horario)
export const getFichajesConfigs = () => api.get('/fichajes/config');
export const getFichajesConfig = (staffId) => api.get(`/fichajes/config/${staffId}`);
export const updateFichajesConfig = (staffId, data) => api.put(`/fichajes/config/${staffId}`, data);
export const clockEvent = (data) => api.post('/fichajes/clock', data);
export const correctEvent = (data) => api.post('/fichajes/correct', data);
export const manualEvent = (data) => api.post('/fichajes/manual', data);
export const deleteClockEvent = (eventId) => api.delete(`/fichajes/events/${eventId}`);
export const getFichajesEvents = (params) => api.get('/fichajes/events', { params });
export const getFichajesStatus = (staffId) => api.get(`/fichajes/status/${staffId}`);
export const getFichajesCalendar = (staffId, params) => api.get(`/fichajes/calendar/${staffId}`, { params });
export const getFichajesDaily = (staffId, date) => api.get(`/fichajes/daily/${staffId}/${date}`);
export const getFichajesMonthly = (staffId, params) => api.get(`/fichajes/monthly/${staffId}`, { params });
export const getFichajesAdminSummary = (params) => api.get('/fichajes/admin/summary', { params });
export const getFichajesAudit = (params) => api.get('/fichajes/audit', { params });

export default api;
